import { Button, Tab, Tabs, useDisclosure } from "@heroui/react";
import { useEffect, useState } from "react";
import { Agreement } from "@/api/agreements";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaPlus, FaPencil } from "react-icons/fa6";
import { api } from "@/api";
import useErrorHandling from "@/hooks/useErrorHandling";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { useTour } from "@/hooks/useTour";
import { InvitationTypeSelectionModal } from "@/app/components/modals/invitation/invitationTypeSelectionModal";
import { useNavigate } from "react-router-dom";

interface TabLocationInterface {
  id: number;
  display_name: string;
}

type TabLocation = TabLocationInterface | null;

export function AgreementsPage() {
  const [rawAgreements, setRawAgreements] = useState<Agreement[] | null>(null);
  const [agreements, setAgreements] = useState<Agreement[] | null>(null);

  const [tabs, setTabs] = useState<TabLocation[] | null>(null);
  const [activeTab, setActiveTab] = useState<any>("all");
  const [displayMode, setDisplayMode] = useState<any>("all");

  const cachedLocations = useAppSelector((state) => state.handbooks.location);
  const cachedOffices = useAppSelector((state) => state.handbooks.office);

  const handleError = useErrorHandling();
  const navigate = useNavigate();

  const invitationTypeSelectionDisclosure = useDisclosure();
  const editAgreementDisclosure = useDisclosure();

  const fullscreenDisclosure = useDisclosure();

  useTour(9);

  useEffect(() => {
    api.agreements
      .list()
      .then(async (response) => {
        setRawAgreements(response);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [editAgreementDisclosure.isOpen, handleError]);

  useEffect(() => {
    const locationMap = new Map<number, TabLocationInterface>();

    rawAgreements?.forEach((agreement) => {
      agreement.offices?.forEach((office) => {
        const nOffice = cachedOffices.find((o) => o.id == office.officeId);
        if (nOffice && nOffice.location) {
          if (!locationMap.has(nOffice.location)) {
            locationMap.set(nOffice.location, {
              id: nOffice.location,
              display_name:
                cachedLocations.find((l) => l.id == nOffice.location)
                  ?.display_name || "Локация " + nOffice.location,
            });
          }
        }
      });
    });

    const locations: TabLocation[] = Array.from(locationMap.values());
    setTabs(locations);
  }, [rawAgreements, cachedOffices, cachedLocations]);

  useEffect(() => {
    if (!rawAgreements) {
      setAgreements(null);
      return;
    }

    let filtered = [...rawAgreements];

    if (activeTab !== "all") {
      filtered = filtered.filter((agreement) => {
        // Проверяем, есть ли хотя бы один офис в нужной локации
        return agreement.offices?.some((officeItem) => {
          const cachedOffice = cachedOffices.find(
            (o) => o.id === officeItem.officeId,
          );
          return cachedOffice?.location == activeTab;
        });
      });
    }

    if (displayMode !== "all") {
      const isActive = displayMode === "active";
      filtered = filtered.filter((office) => office.isActive === isActive);
    }

    setAgreements(filtered);
  }, [rawAgreements, activeTab, displayMode, cachedOffices]);

  const handleSelectInvitationType = (
    type: "existing_renter" | "new_renter" | "cancel",
  ) => {
    if (type === "cancel") return;
    if (type === "existing_renter") {
      navigate("/dashboard/invitations/existing/create");
    } else if (type === "new_renter") {
      navigate("/dashboard/invitations/new/create");
    }
  };

  return (
    <>
      <InvitationTypeSelectionModal
        disclosure={invitationTypeSelectionDisclosure}
        onSelectType={handleSelectInvitationType}
      />
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium" id="agreements-title">
                Договора
              </span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaPlus />}
                color="primary"
                variant="bordered"
                className="max-w-fit"
                onClick={() => invitationTypeSelectionDisclosure.onOpen()}>
                Создать приглашение
              </Button>
            </div>
          </div>
          <div className="flex flex-row gap-2 mb-4">
            <Tabs
              color="primary"
              variant="bordered"
              selectedKey={activeTab}
              onSelectionChange={setActiveTab}>
              <Tab key="all" title="Все локации" />
              {tabs?.map((tab) => (
                <Tab key={tab?.id} title={tab?.display_name}></Tab>
              ))}
            </Tabs>
            <Tabs
              color="primary"
              variant="bordered"
              selectedKey={displayMode}
              onSelectionChange={setDisplayMode}
              aria-label="Статус активности">
              <Tab key="all" title="Все" />
              <Tab key="active" title="Активные" />
              <Tab key="inactive" title="Неактивные" />
            </Tabs>
          </div>

          <div className="flex flex-col gap-2 mb-2">
            <div id="agreements-table">
              <TableBuilder
                displayOptions={false}
                removeWrapper={true}
                fullscreenDisclosure={fullscreenDisclosure}
                columns={[
                  {
                    label: "Арендатор",
                    key: "renterId",
                    type: ColumnType.Renter,
                    sortable: true,
                    sortKey: "renterDisplayName",
                    searchKey: "renterDisplayName",
                  },
                  {
                    label: "Цена",
                    key: "price",
                    type: ColumnType.Number,
                    sortable: true,
                  },
                  {
                    label: "Описание",
                    key: "description",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    label: "Депозит",
                    key: "deposit",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    label: "Статус",
                    key: "isActive",
                    type: ColumnType.Boolean,
                    sortable: true,
                  },
                  {
                    label: "Дата начала",
                    key: "startDate",
                    type: ColumnType.Date,
                    sortable: true,
                  },
                  {
                    label: "Дата окончания",
                    key: "endDate",
                    type: ColumnType.Date,
                    sortable: true,
                  },
                  {
                    key: "actions",
                    label: "Действия",
                    type: ColumnType.Actions,
                    actions: [
                      {
                        icon: <FaPencil />,
                        onClick: (_value, row) => {
                          navigate(`/dashboard/agreements/${row.id}/edit`);
                        },
                      },
                    ],
                  },
                ]}
                data={agreements || []}
                rowsPerPage={10}
                defaultSortDescriptor={{
                  column: "office",
                  direction: "ascending",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
